import { Info } from "lucide-react";
import { getProposalTexts, type ProposalLanguage } from "./i18n";

export type BrandSectionId =
  | "dashboard"
  | "integrations"
  | "ai_agent"
  | "vision"
  | "feedback_loop";

export const BRAND_SECTION_IDS: BrandSectionId[] = [
  "dashboard",
  "integrations",
  "ai_agent",
  "feedback_loop",
];

function HeadlineParts({
  parts,
}: {
  parts: Array<{ text: string; gradient?: boolean }>;
}) {
  return (
    <>
      {parts.map((part, i) =>
        part.gradient ? (
          <span key={i} className="brand-gradient-text">
            {part.text}
          </span>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </>
  );
}

export function BrandVision({
  num,
  language,
}: {
  num: string;
  language: ProposalLanguage;
}) {
  const t = getProposalTexts(language).brandSections.vision;
  void num;
  return (
    <section>
      <div
        className="relative overflow-hidden text-white"
        style={{ background: "#0E0E12" }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 80% 20%, rgba(30,88,255,0.45) 0%, rgba(0,252,203,0.14) 38%, transparent 70%)",
            filter: "blur(2px)",
          }}
        />
        <div className="relative mx-auto max-w-5xl px-5 py-14 sm:px-10 sm:py-20">
          <div className="flex items-center gap-3">
            <span className="h-[2px] w-9 rounded-full" style={{ background: "linear-gradient(90deg, #1E58FF 0%, #00FCCB 100%)" }} />
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400 sm:text-[11px]">
              {t.eyebrow}
            </span>
          </div>
          <h2 className="mt-7 max-w-3xl font-display text-[34px] font-extrabold leading-[1.05] tracking-tightest sm:text-5xl md:text-6xl">
            <HeadlineParts parts={t.headlineParts} />
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-zinc-400 sm:text-base">
            {t.body}
          </p>
          <div className="mt-10 grid gap-6 sm:grid-cols-3 sm:gap-8">
            {t.bullets.map((b, i) => (
              <div key={i} className="border-t border-white/15 pt-5">
                <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-2 text-base font-semibold text-white">{b.title}</div>
                <div className="mt-2 text-[13.5px] leading-relaxed text-zinc-400">{b.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function BrandDashboard({
  num,
  language,
}: {
  num: string;
  language: ProposalLanguage;
}) {
  const t = getProposalTexts(language).brandSections.dashboard;
  void num;
  return (
    <section>
      <div className="bg-[#FAF7F1]">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:px-10 sm:py-20">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 sm:text-[11px]">
            {t.eyebrow}
          </div>
          <h2 className="mt-6 max-w-3xl font-display text-[34px] font-extrabold leading-[1.05] tracking-tightest text-zinc-900 sm:text-5xl">
            <HeadlineParts parts={t.headlineParts} />
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-zinc-600 sm:text-base">
            {t.body}
          </p>
          <figure className="mt-10 overflow-hidden rounded-2xl bg-white ring-1 ring-zinc-200/70">
            <div className="flex items-center gap-1.5 border-b border-zinc-200/70 px-4 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
              <span className="ml-3 font-mono text-[10px] text-zinc-500 sm:text-[11px]">
                app.noium.com
              </span>
            </div>
            <img
              src="/assets/product-ui.png"
              alt={t.imageAlt}
              className="block w-full"
              loading="lazy"
            />
          </figure>
        </div>
      </div>
    </section>
  );
}

const INTEGRATION_LOGOS: Array<{ src: string; alt: string }> = [
  { src: "/assets/logos/rivile.png", alt: "Rivile" },
  { src: "/assets/logos/finvalda.png", alt: "Finvalda" },
  { src: "/assets/logos/centas.png", alt: "Centas" },
  { src: "/assets/logos/butenta.png", alt: "Būtenta" },
  { src: "/assets/logos/ivesk.png", alt: "įvesk.lt" },
  { src: "/assets/logos/site-pro.png", alt: "Site.pro" },
  { src: "/assets/logos/sharepoint.png", alt: "SharePoint" },
  { src: "/assets/logos/google-drive.png", alt: "Google Drive" },
  { src: "/assets/logos/business-central.png", alt: "Microsoft Dynamics 365 BC" },
  { src: "/assets/logos/debetas.png", alt: "Debetas" },
];

export function BrandIntegrations({
  num,
  language,
}: {
  num: string;
  language: ProposalLanguage;
}) {
  const t = getProposalTexts(language).brandSections.integrations;
  void num;
  return (
    <section>
      <div
        className="relative overflow-hidden text-white"
        style={{ background: "#0E0E12" }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 60%, rgba(30,88,255,0.32) 0%, rgba(0,252,203,0.10) 40%, transparent 75%)",
            filter: "blur(2px)",
          }}
        />
        <div className="relative mx-auto max-w-5xl px-5 py-14 sm:px-10 sm:py-20">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400 sm:text-[11px]">
            {t.eyebrow}
          </div>
          <h2 className="mt-6 max-w-3xl font-display text-[34px] font-extrabold leading-[1.05] tracking-tightest sm:text-5xl">
            <HeadlineParts parts={t.headlineParts} />
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-zinc-400 sm:text-base">
            {t.body}
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-3 sm:gap-5">
            {t.bullets.map((b, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white/[0.04] p-5 ring-1 ring-white/10 backdrop-blur-sm"
              >
                <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
                  {b.tag}
                </div>
                <div className="mt-2 text-base font-semibold text-white">{b.title}</div>
                <div className="mt-2 text-[13.5px] leading-relaxed text-zinc-400">{b.body}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-5 sm:gap-4">
            {INTEGRATION_LOGOS.map((logo) => (
              <div
                key={logo.alt}
                className="flex h-20 items-center justify-center rounded-xl bg-white p-3 sm:aspect-[3/2] sm:h-auto sm:p-5"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="max-h-9 max-w-full object-contain sm:max-h-full"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-end justify-between gap-3 border-t border-white/10 pt-6 text-[11px] text-zinc-400">
            <p className="max-w-xl leading-relaxed text-zinc-400">{t.footnote}</p>
            <div className="font-mono text-[10px] uppercase tracking-[0.14em]">
              REST · WEBHOOK · OAUTH 2.0
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PortalMockup({ language }: { language: ProposalLanguage }) {
  const t = getProposalTexts(language).brandSections.feedback_loop;
  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-zinc-200/70 shadow-sm">
      <div className="flex items-center gap-1.5 border-b border-zinc-200/70 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
        <span className="ml-3 font-mono text-[10px] text-zinc-500 sm:text-[11px]">
          feedback.noium.com
        </span>
      </div>
      <div className="space-y-4 p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-sm font-bold tracking-tightest text-zinc-900">
            Noium{t.mockupFunkcijos}
          </span>
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-zinc-600">
            15
          </span>
          <span className="ml-auto rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
            + {t.mockupSubmit}
          </span>
        </div>
        <div className="rounded-xl bg-zinc-50 p-4 ring-1 ring-zinc-200/70">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
            ● {t.mockupWeAsk}
          </div>
          <div className="mt-2 font-display text-base font-bold text-zinc-900">
            {t.mockupTitle}
          </div>
          <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-600">
            {t.mockupSub}
          </p>
        </div>
        {t.mockupItems.map((item, i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-3 rounded-lg bg-white p-3 ring-1 ring-zinc-200/70"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                  {item.tagA}
                </span>
                <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-600">
                  {item.tagB}
                </span>
              </div>
              <div className="mt-1 truncate text-[13px] font-semibold text-zinc-900">
                {item.title}
              </div>
            </div>
            <div className="flex flex-col items-center rounded-md bg-zinc-50 px-2 py-1 text-[11px] font-semibold text-zinc-700 ring-1 ring-zinc-200/70">
              <span>▲</span>
              <span>{item.votes}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-zinc-200/70 bg-zinc-900 px-5 py-3 text-[11px] text-zinc-300">
        <span className="font-mono uppercase tracking-[0.14em] text-zinc-500">{t.mockupRealtime}:</span>{" "}
        {t.mockupRealtimeBody}
      </div>
    </div>
  );
}

export function BrandFeedbackLoop({
  num,
  language,
}: {
  num: string;
  language: ProposalLanguage;
}) {
  const t = getProposalTexts(language).brandSections.feedback_loop;
  void num;
  return (
    <section>
      <div className="bg-[#FAF7F1]">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:px-10 sm:py-20">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 sm:text-[11px]">
            {t.eyebrow}
          </div>
          <div className="mt-8 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-start">
            <div>
              <h2 className="max-w-2xl font-display text-[34px] font-extrabold leading-[1.05] tracking-tightest text-zinc-900 sm:text-5xl">
                <HeadlineParts parts={t.headlineParts} />
              </h2>
              <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-zinc-600 sm:text-base">
                {t.body}
              </p>
              <div className="mt-8 space-y-5">
                {t.bullets.map((b, i) => (
                  <div key={i} className="flex items-start gap-4 border-t border-zinc-200 pt-5">
                    <span className="font-mono-tight mt-[3px] shrink-0 text-[11px] font-bold tabular-nums tracking-[0.05em] text-zinc-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0">
                      <div className="text-base font-semibold text-zinc-900">{b.title}</div>
                      <div className="mt-1 text-[13.5px] leading-relaxed text-zinc-600">
                        {b.body}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <a
                  href="https://feedback.noium.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-5 py-3 text-[13.5px] font-semibold text-white transition-colors hover:bg-zinc-700"
                >
                  {t.ctaButton}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
                    <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h4a.75.75 0 0 1 0 1.5h-4Zm6.5-.75a.75.75 0 0 1 .75-.75h4.75a.75.75 0 0 1 .75.75v4.75a.75.75 0 0 1-1.5 0V6.31l-6.22 6.22a.75.75 0 0 1-1.06-1.06L14.44 5.5h-3.44a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
            <PortalMockup language={language} />
          </div>
        </div>
      </div>
    </section>
  );
}

function AgentChatMockup({ language }: { language: ProposalLanguage }) {
  const t = getProposalTexts(language).brandSections.ai_agent;
  return (
    <div className="overflow-hidden rounded-2xl bg-[#13131A] shadow-2xl ring-1 ring-white/10">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[11px] font-bold text-white shadow-sm"
            style={{ background: "linear-gradient(135deg, #AF7BF7 0%, #1E58FF 100%)" }}
            aria-hidden
          >
            N
          </span>
          <div>
            <div className="text-[13px] font-semibold text-white">{t.chatTitle}</div>
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden />
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-300/80">
                {t.chatStatus}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5" aria-hidden>
          <span className="h-2 w-2 rounded-full bg-white/15" />
          <span className="h-2 w-2 rounded-full bg-white/15" />
          <span className="h-2 w-2 rounded-full bg-white/15" />
        </div>
      </div>
      <div className="space-y-3 px-4 py-5">
        {t.chatMessages.map((m, i) => {
          const isAgent = m.role === "agent";
          return (
            <div
              key={i}
              className={`flex ${isAgent ? "justify-start" : "justify-end"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                  isAgent
                    ? "bg-white/[0.06] text-zinc-100 ring-1 ring-white/10"
                    : "text-white shadow-sm"
                }`}
                style={
                  isAgent
                    ? undefined
                    : { background: "linear-gradient(135deg, #1E58FF 0%, #00FCCB 100%)" }
                }
              >
                {m.text}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 border-t border-white/10 bg-white/[0.02] px-4 py-3">
        <div className="flex-1 truncate rounded-full bg-white/[0.06] px-3 py-2 text-[12px] text-zinc-500 ring-1 ring-white/10">
          {t.chatPlaceholder}
        </div>
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow-sm"
          style={{ background: "linear-gradient(135deg, #AF7BF7 0%, #1E58FF 100%)" }}
          aria-hidden
        >
          <svg
            viewBox="0 0 20 20"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 10h12" />
            <path d="M11 5l5 5-5 5" />
          </svg>
        </span>
      </div>
    </div>
  );
}

export function BrandAiAgent({
  num,
  language,
}: {
  num: string;
  language: ProposalLanguage;
}) {
  const t = getProposalTexts(language).brandSections.ai_agent;
  void num;
  return (
    <section>
      <div
        className="relative overflow-hidden text-white"
        style={{ background: "#0E0E12" }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 20% 50%, rgba(175,123,247,0.40) 0%, rgba(30,88,255,0.18) 35%, transparent 70%)",
            filter: "blur(2px)",
          }}
        />
        <div className="relative mx-auto max-w-5xl px-5 py-14 sm:px-10 sm:py-20">
          <div className="flex items-center gap-3">
            <span
              className="h-[2px] w-9 rounded-full"
              style={{ background: "linear-gradient(90deg, #AF7BF7 0%, #1E58FF 100%)" }}
            />
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400 sm:text-[11px]">
              {t.eyebrow}
            </span>
          </div>
          <div className="mt-7 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-start">
            <div>
              <h2 className="max-w-2xl font-display text-[34px] font-extrabold leading-[1.05] tracking-tightest sm:text-5xl md:text-6xl">
                <HeadlineParts parts={t.headlineParts} />
              </h2>
              <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-zinc-400 sm:text-base">
                {t.body}
              </p>
              <div className="mt-6 flex max-w-2xl items-start gap-3 rounded-xl bg-white/[0.04] p-4 ring-1 ring-white/10">
                <Info
                  className="mt-0.5 h-4 w-4 shrink-0 text-[#00FCCB]"
                  aria-hidden
                />
                <p className="text-[13px] leading-relaxed text-zinc-300 sm:text-[13.5px]">
                  {t.note}
                </p>
              </div>
            </div>
            <AgentChatMockup language={language} />
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-3 sm:gap-5">
            {t.capabilities.map((cap, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white/[0.04] p-5 ring-1 ring-white/10 backdrop-blur-sm"
              >
                <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
                  {cap.tag}
                </div>
                <div className="mt-2 text-base font-semibold text-white">{cap.title}</div>
                <div className="mt-2 text-[13.5px] leading-relaxed text-zinc-400">
                  {cap.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export const BRAND_SECTION_COMPONENTS: Record<
  BrandSectionId,
  React.ComponentType<{ num: string; language: ProposalLanguage }>
> = {
  dashboard: BrandDashboard,
  integrations: BrandIntegrations,
  ai_agent: BrandAiAgent,
  vision: BrandVision,
  feedback_loop: BrandFeedbackLoop,
};
